import { unwindEdges } from "@good-idea/unwind-edges";
import { wait } from "../../utils";
import { fetchWithMemoryCache } from "../../utils/cache/memory";
import { createHashKey } from "../../utils";
import { convertShopifyNumberIdToPath } from "../../utils/shopify/transform";
import { ShopifySdk } from ".";
import { rateLimitRequests } from "./client";
import { withCountryCode } from "../../types/fetch";

const DEFAULT_MAX_SIZE = 1000;
interface ResultProps {
  results: {
    entries: any[];
    productCursor?: any;
  };
}

// Main product fetch
export async function productById(
  id: string | number,
  { countryCode }: withCountryCode
) {
  const shopifyId =
    typeof id == "string" ? id : convertShopifyNumberIdToPath(id);

  const fetcher = () => ShopifySdk.productById({ id: shopifyId, countryCode });
  const key = createHashKey("shopifyProductById", { shopifyId, countryCode });
  const response = await fetchWithMemoryCache(key, fetcher, 2);
  return response.product;
}

// Collection products
export async function simpleProductsByIds(
  ids: string[] | number[],
  { countryCode }: withCountryCode
) {
  const shopifyIds = ids.map((id) =>
    typeof id == "string" ? id : convertShopifyNumberIdToPath(id)
  );

  const promises = shopifyIds.map(async (shopifyId) => {
    const fetcher: any = () =>
      ShopifySdk.simpleProductById({ id: shopifyId, countryCode });
    const key = createHashKey("simpleShopifyProductById", {
      shopifyId,
      countryCode,
    });
    const response = await fetchWithMemoryCache(key, fetcher, 2);
    return response.product;
  });

  const products = await rateLimitRequests(promises);

  return products.filter((product) => !!product);
}

// Only used for Klaviyo feed
export async function productsAll({ countryCode }: any) {
  const fetcher: any = async ({ results }: ResultProps) => {
    const { entries, productCursor } = results;
    const response = await ShopifySdk.products({
      productCursor,
      countryCode,
    });

    // Return results so far if fetch fails
    if (!response.products) return results;

    // Return results so far if fetch contains no more products
    const [products, pagination] = unwindEdges(response.products);
    const hasNextPage = pagination.pageInfo?.hasNextPage;
    if (products.length === 0) return results;

    //
    const newResults = {
      entries: [...results.entries, ...products],
      productCursor: pagination.lastCursor,
    };

    if (!!hasNextPage && results.entries.length < DEFAULT_MAX_SIZE) {
      await wait(500);
      return fetcher({
        results: newResults,
      });
    }

    return newResults;
  };

  const allProducts = await fetcher({
    results: {
      entries: [],
    },
  });

  return allProducts;
}
